// @refresh reset
/**
 * useDashboardState — all reactive state for the Dashboard page.
 *
 * Extracted from DashboardPage.tsx to reduce the God Component.
 * Handles: UI state, data loading, derived computations, presence
 * polling (50-second interval), favorites (server-synced), keyboard shortcuts.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  listTemplates,
  type TemplateMeta,
} from "../utils/templateApi";
import { listUsers, type UserRecord, getUserRoles } from "../utils/userApi";
import { useAuth } from "../components/shared/AuthContext";
import { getProfile } from "../utils/profileApi";
import { getAllPresence, type PresenceEntry } from "../utils/presenceApi";
import { apiFetch } from "../utils/apiClient";
import { cacheSet, cacheGetStale } from "../utils/offlineCache";
import { listFolders, listTagDefs, type FolderDef, type TagDef } from "../utils/folderTagApi";
import { useTheme } from "../components/shared/ThemeContext";
import {
  readLocalFavorites,
  loadServerFavorites,
  saveServerFavorites,
} from "../utils/favoritesApi";
import type {
  DashboardTab,
  ViewMode,
  FilterType,
  SortKey,
} from "../components/dashboard/dashboardConstants";

/* ================================================================
   Offline cache keys
   ================================================================ */
const CACHE_PREFIX = "zap_cache:"; // must match offlineCache.ts
const CACHE_KEY_TEMPLATES = "dashboard_templates";
const CACHE_KEY_COMMENT_STATS = "dashboard_comment_stats";

/* ================================================================
   Helper: map restricted role -> template assignment field
   ================================================================ */
function getAssignmentField(role: string): keyof TemplateMeta | null {
  switch (role) {
    case "designer": return "designer";
    case "content_writer": return "contentWriter";
    case "developer": return "developer";
    case "marketer": return "marketer";
    case "team_lead": return "teamLead";
    default: return null;
  }
}

/* ================================================================
   Hook
   ================================================================ */

export function useDashboardState() {
  const auth = useAuth();
  const {
    user,
    logout,
    isAdmin,
    isReady,
    canManageUsers,
    canCreateTemplates,
    canDeleteTemplates,
    canDeleteMasters,
    canEditTemplateMeta,
    hasFullAccess,
  } = auth;
  const { isDark, toggle: toggleTheme } = useTheme();

  // ---- Core data ----
  // Optimistic init: if we have cached templates in localStorage, show them
  // immediately while the fresh API call happens in the background.
  const [templates, setTemplates] = useState<TemplateMeta[]>(() => {
    try {
      const raw = localStorage.getItem(CACHE_PREFIX + CACHE_KEY_TEMPLATES);
      if (raw) {
        const entry = JSON.parse(raw);
        if (entry?.data && Array.isArray(entry.data) && entry.data.length > 0) {
          console.log("[Dashboard] Optimistic init — showing", entry.data.length, "cached templates");
          return entry.data;
        }
      }
    } catch { /* ignore */ }
    return [];
  });
  // If we had cached templates, skip the skeleton
  const [isLoading, setIsLoading] = useState(() => {
    try {
      const raw = localStorage.getItem(CACHE_PREFIX + CACHE_KEY_TEMPLATES);
      if (raw) {
        const entry = JSON.parse(raw);
        if (entry?.data && Array.isArray(entry.data) && entry.data.length > 0) {
          return false; // skip loading skeleton
        }
      }
    } catch { /* ignore */ }
    return true;
  });
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [folders, setFolders] = useState<FolderDef[]>([]);
  const [tagDefs, setTagDefs] = useState<TagDef[]>([]);

  /** Whether the currently displayed data came from offline cache */
  const [isStaleData, setIsStaleData] = useState(false);

  // ---- UI state ----
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [shareInfo, setShareInfo] = useState<{ id: string } | null>(null);
  const [activeTab, setActiveTab] = useState<DashboardTab>("templates");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt-desc");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [filterTeamMember, setFilterTeamMember] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterFavoritesOnly, setFilterFavoritesOnly] = useState(false);
  const [filterFolder, setFilterFolder] = useState<string>("all");
  const [filterProjectType, setFilterProjectType] = useState<string>("all");
  const [showFolderTagManager, setShowFolderTagManager] = useState(false);
  const [whiteLabelTemplate, setWhiteLabelTemplate] = useState<TemplateMeta | null>(null);

  // ---- Content completion + comment stats ----
  const [commentStats, setCommentStats] = useState<Record<string, { total: number; open: number; resolved: number }>>({});

  // ---- Global search modal ----
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);

  // ---- Batch selection ----
  const [batchSelected, setBatchSelected] = useState<Set<string>>(new Set());
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [showBulkAiModal, setShowBulkAiModal] = useState(false);

  // ---- Presence (50-second polling — lowered from 20s) ----
  // Deferred: first poll starts after 3 seconds to avoid competing
  // with critical data loads (templates, users, folders).
  const [presenceMap, setPresenceMap] = useState<Map<string, PresenceEntry[]>>(new Map());

  useEffect(() => {
    if (!user) return;
    let active = true;
    const poll = () => {
      getAllPresence().then((users) => {
        if (!active) return;
        const map = new Map<string, PresenceEntry[]>();
        users.forEach((u) => {
          if (u.username === user.username) return;
          const list = map.get(u.templateId) || [];
          list.push(u);
          map.set(u.templateId, list);
        });
        setPresenceMap(map);
      }).catch(() => {});
    };
    // Delay first poll by 3s so initial render + critical loads aren't blocked
    const initDelay = setTimeout(() => {
      if (!active) return;
      poll();
    }, 3000);
    const id = setInterval(poll, 50_000);
    return () => { active = false; clearTimeout(initDelay); clearInterval(id); };
  }, [user]);

  // ---- User menu dropdown ----
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userAvatar, setUserAvatar] = useState<string | undefined>(undefined);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Deferred: avatar load starts after 2s — purely cosmetic
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const t = setTimeout(() => {
      getProfile(user.username).then((p) => {
        if (!cancelled && p?.avatarUrl) setUserAvatar(p.avatarUrl);
      }).catch(() => {});
    }, 2000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [user]);

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showUserMenu]);

  // ==============================================================
  //  Favorites — server-synced with localStorage mirror
  // ==============================================================
  // 1) Instant init from localStorage (no flicker)
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    if (!user) return new Set();
    return new Set(readLocalFavorites(user.username));
  });

  // 2) On mount, fetch authoritative list from server and merge
  // Deferred by 5 seconds — favorites from localStorage are shown instantly
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const delay = setTimeout(() => {
      loadServerFavorites(user.username).then((serverIds) => {
        if (cancelled) return;
        setFavorites((prev) => {
          const merged = new Set([...serverIds, ...prev]);
          if (prev.size === 0 || serverIds.length === 0) {
            return serverIds.length > 0 ? new Set(serverIds) : prev;
          }
          return merged;
        });
      }).catch(() => {});
    }, 5000);
    return () => { cancelled = true; clearTimeout(delay); };
  }, [user]);

  // 3) Toggle: update state + fire-and-forget server sync
  const toggleFavorite = useCallback((id: string) => {
    if (!user) return;
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      // Sync to server (fire-and-forget) + localStorage mirror
      saveServerFavorites(user.username, [...next]).catch(() => {});
      return next;
    });
  }, [user]);

  // ---- Keyboard shortcut: Ctrl+Shift+K -> global search ----
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "K") {
        e.preventDefault();
        setShowGlobalSearch((prev) => !prev);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // ---- Data loaders ----
  const loadTemplates = useCallback(async () => {
    try {
      const data = await listTemplates();
      setTemplates(data);
      setIsStaleData(false);
      // Cache for offline fallback — 24 hour TTL (stale data is always
      // shown optimistically on next visit; background refresh updates it)
      cacheSet(CACHE_KEY_TEMPLATES, data, 24 * 60 * 60 * 1000);
    } catch (err) {
      console.error("[Dashboard] Failed to load templates:", err);

      // Offline fallback: try stale cache
      const stale = cacheGetStale<TemplateMeta[]>(CACHE_KEY_TEMPLATES);
      if (stale && stale.data.length > 0) {
        setTemplates(stale.data);
        setIsStaleData(true);
        const ageMin = Math.round(stale.age / 60_000);
        toast.warning(
          `\u05DE\u05D5\u05E6\u05D2\u05D9\u05DD \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD \u05E9\u05DE\u05D5\u05E8\u05D9\u05DD (${ageMin} \u05D3\u05E7\u05D5\u05EA). \u05D7\u05DC\u05E7 \u05DE\u05D4\u05E0\u05EA\u05D5\u05E0\u05D9\u05DD \u05E2\u05E9\u05D5\u05D9\u05D9\u05DD \u05DC\u05D0 \u05DC\u05D4\u05D9\u05D5\u05EA \u05E2\u05D3\u05DB\u05E0\u05D9\u05D9\u05DD.`,
          { duration: 6000 },
        );
      } else {
        toast.error("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D8\u05E2\u05D9\u05E0\u05EA \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await listUsers();
      setAllUsers(data);
    } catch (err) {
      console.error("[Dashboard] Failed to load users:", err);
    }
  }, []);

  const loadFoldersAndTags = useCallback(async () => {
    try {
      const [f, t] = await Promise.all([listFolders(), listTagDefs()]);
      setFolders(f);
      setTagDefs(t);
    } catch (err) {
      console.error("[Dashboard] Failed to load folders/tags:", err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadTemplates();
      loadUsers();
      loadFoldersAndTags();
    }
  }, [user, loadTemplates, loadUsers, loadFoldersAndTags]);

  // ---- When we come back online, reload fresh data ----
  useEffect(() => {
    const handleOnline = () => {
      if (isStaleData) {
        console.log("[Dashboard] Back online \u2014 refreshing stale data...");
        loadTemplates();
        loadUsers();
      }
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [isStaleData, loadTemplates, loadUsers]);

  // ---- Derived: accessible templates ----
  const accessibleTemplates = useMemo(() => {
    if (!user) return [];
    const nonMasters = templates.filter((t) => !t.isMaster && !t.archived);
    if (hasFullAccess) return nonMasters;
    const allUserRoles = getUserRoles(user);
    const assignmentFields = allUserRoles
      .map((r) => getAssignmentField(r))
      .filter((f): f is keyof TemplateMeta => f !== null);
    if (assignmentFields.length > 0) {
      return nonMasters.filter((t) =>
        assignmentFields.some((field) => (t as any)[field] === user.username)
      );
    }
    if (user.templateAccess === "all") return nonMasters;
    return nonMasters.filter((t) => (user.templateAccess as string[]).includes(t.id));
  }, [templates, user, hasFullAccess]);

  const archivedTemplates = useMemo(() => {
    if (!user) return [];
    return templates.filter((t) => !t.isMaster && t.archived);
  }, [templates, user]);

  const masterTemplates = useMemo(() => templates.filter((t) => t.isMaster), [templates]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    accessibleTemplates.forEach((t) => {
      (t.tags || []).forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b, "he"));
  }, [accessibleTemplates]);

  const allTeamMembers = useMemo(() => {
    const nameSet = new Set<string>();
    accessibleTemplates.forEach((t) => {
      [t.characterizer, t.marketer, t.designer, t.developer, t.contentWriter, t.teamLead].forEach((uname) => {
        if (uname && uname.trim()) {
          const u = allUsers.find((usr) => usr.username === uname);
          nameSet.add(u ? u.displayName : uname);
        }
      });
    });
    return Array.from(nameSet).sort((a, b) => a.localeCompare(b, "he"));
  }, [accessibleTemplates, allUsers]);

  // ---- Sort & filter ----
  const sortedAndFiltered = useMemo(() => {
    let result = accessibleTemplates;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const resolve = (uname: string) => {
        if (!uname) return "";
        const u = allUsers.find((usr) => usr.username === uname);
        return u ? u.displayName.toLowerCase() : uname.toLowerCase();
      };
      result = result.filter((t) =>
        t.clientName.toLowerCase().includes(q) ||
        t.clientNumber.toLowerCase().includes(q) ||
        resolve(t.characterizer).includes(q) ||
        resolve(t.marketer).includes(q) ||
        resolve(t.designer).includes(q) ||
        resolve(t.developer).includes(q) ||
        resolve(t.contentWriter || "").includes(q) ||
        resolve(t.teamLead || "").includes(q)
      );
    }
    if (filterType !== "all") {
      result = result.filter((t) => (t.templateType || "branding") === filterType);
    }
    if (selectedTags.length > 0) {
      result = result.filter((t) =>
        selectedTags.some((tag) => (t.tags || []).includes(tag))
      );
    }
    if (filterTeamMember) {
      result = result.filter((t) => {
        const names = [t.characterizer, t.marketer, t.designer, t.developer, t.contentWriter, t.teamLead].map((uname) => {
          if (!uname) return "";
          const u = allUsers.find((usr) => usr.username === uname);
          return u ? u.displayName : uname;
        });
        return names.some((name) => name.trim() === filterTeamMember);
      });
    }
    if (filterStatus !== "all") {
      result = result.filter((t) => (t.status || "draft") === filterStatus);
    }
    if (filterFolder !== "all") {
      if (filterFolder === "__none") {
        result = result.filter((t) => !t.folderId);
      } else {
        result = result.filter((t) => t.folderId === filterFolder);
      }
    }
    if (filterProjectType !== "all") {
      if (filterProjectType === "__none") {
        result = result.filter((t) => !t.projectType);
      } else {
        result = result.filter((t) => t.projectType === filterProjectType);
      }
    }
    if (filterFavoritesOnly) {
      result = result.filter((t) => favorites.has(t.id));
    }
    const [field, dir] = sortKey.split("-") as [string, string];
    result = [...result].sort((a, b) => {
      const aFav = favorites.has(a.id) ? 1 : 0;
      const bFav = favorites.has(b.id) ? 1 : 0;
      if (aFav !== bFav) return bFav - aFav;
      let cmp = 0;
      if (field === "clientName") {
        cmp = a.clientName.localeCompare(b.clientName, "he");
      } else if (field === "createdAt") {
        cmp = (a.createdAt || 0) - (b.createdAt || 0);
      } else {
        cmp = (a.updatedAt || 0) - (b.updatedAt || 0);
      }
      return dir === "asc" ? cmp : -cmp;
    });
    return result;
  }, [accessibleTemplates, searchQuery, filterType, sortKey, selectedTags, filterTeamMember, filterStatus, filterFavoritesOnly, filterFolder, filterProjectType, favorites, allUsers]);

  // ---- Stats ----
  const stats = useMemo(() => {
    const total = accessibleTemplates.length;
    const brandingCount = accessibleTemplates.filter((t) => (t.templateType || "branding") === "branding").length;
    const storeCount = accessibleTemplates.filter((t) => (t.templateType || "branding") === "store").length;
    const faceliftCount = accessibleTemplates.filter((t) => (t.templateType || "branding") === "facelift").length;
    const lastUpdated = accessibleTemplates.length > 0
      ? Math.max(...accessibleTemplates.map((t) => t.updatedAt || 0))
      : 0;
    const mastersCount = masterTemplates.length;
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recentlyUpdated = accessibleTemplates.filter((t) => (t.updatedAt || 0) >= sevenDaysAgo).length;
    const masterBased = accessibleTemplates.filter((t) => !!t.masterSourceId).length;
    return { total, brandingCount, storeCount, faceliftCount, lastUpdated, mastersCount, recentlyUpdated, masterBased };
  }, [accessibleTemplates, masterTemplates]);

  // ---- Load comment stats (with offline cache) ----
  // Deferred by 4 seconds — not critical for initial render
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const delay = setTimeout(() => {
      apiFetch("/comments/stats")
        .then((data) => {
          if (!cancelled && data.stats) {
            setCommentStats(data.stats);
            cacheSet(CACHE_KEY_COMMENT_STATS, data.stats, 60 * 60 * 1000);
          }
        })
        .catch(() => {
          const stale = cacheGetStale<Record<string, { total: number; open: number; resolved: number }>>(CACHE_KEY_COMMENT_STATS);
          if (!cancelled && stale) {
            setCommentStats(stale.data);
            console.log("[Dashboard] Comment stats loaded from offline cache");
          }
        });
    }, 4000);
    return () => { cancelled = true; clearTimeout(delay); };
  }, [user]);

  // ---- Tab visibility ----
  const showMastersTab = canEditTemplateMeta;
  const showUsersTab = canManageUsers;
  const showArchiveTab = canDeleteTemplates;
  const showTabs = showMastersTab || showUsersTab || showArchiveTab;

  return {
    // Auth
    user, logout, isAdmin, isReady,
    canManageUsers, canCreateTemplates, canDeleteTemplates,
    canDeleteMasters, canEditTemplateMeta, hasFullAccess,
    // Theme
    isDark, toggleTheme,
    // Data
    templates, setTemplates,
    allUsers, folders, tagDefs,
    isLoading, isStaleData,
    // Loaders
    loadTemplates, loadUsers, loadFoldersAndTags,
    // UI state
    searchQuery, setSearchQuery,
    showCreateForm, setShowCreateForm,
    actionLoading, setActionLoading,
    deleteConfirm, setDeleteConfirm,
    editingId, setEditingId,
    shareInfo, setShareInfo,
    activeTab, setActiveTab,
    viewMode, setViewMode,
    filterType, setFilterType,
    sortKey, setSortKey,
    selectedTags, setSelectedTags,
    filterTeamMember, setFilterTeamMember,
    filterStatus, setFilterStatus,
    filterFavoritesOnly, setFilterFavoritesOnly,
    filterFolder, setFilterFolder,
    filterProjectType, setFilterProjectType,
    showFolderTagManager, setShowFolderTagManager,
    whiteLabelTemplate, setWhiteLabelTemplate,
    // Completion & comments
    commentStats,
    // Global search
    showGlobalSearch, setShowGlobalSearch,
    // Batch
    batchSelected, setBatchSelected,
    batchProcessing, setBatchProcessing,
    showBulkAiModal, setShowBulkAiModal,
    // Presence
    presenceMap,
    // User menu
    showUserMenu, setShowUserMenu,
    userAvatar, userMenuRef,
    // Favorites
    favorites, toggleFavorite,
    // Derived
    accessibleTemplates, archivedTemplates, masterTemplates,
    allTags, allTeamMembers,
    sortedAndFiltered, stats,
    // Tab visibility
    showMastersTab, showUsersTab, showArchiveTab, showTabs,
  };
}